import { computed, inject, Type } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withFeature,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { DEFAULT_TASK_DETAIL_STATE } from '@data-access/tasks/constants';
import { TaskDetailSignalState } from '@data-access/tasks/types';
import { TasksService } from '@data-access/tasks/services';
import { Events, on, withReducer } from '@ngrx/signals/events';
import { TASK_DETAIL_EVENTS } from '../signal-events';
import { withTaskDetailEffects } from './effects';
import { withDevtools } from '@angular-architects/ngrx-toolkit';

export const TASK_DETAIL_SIGNAL_STORE = signalStore(
  withDevtools('taskDetails'), // Added for Redux DevTools integration
  withState(() => inject(DEFAULT_TASK_DETAIL_STATE)),
  withProps((store, _taskListService = inject(TasksService)) => ({
    _events: inject(Events),
    _taskListService,
    store,
  })),
  withFeature(({ _events, _taskListService }) =>
    withTaskDetailEffects(_events, _taskListService),
  ),
  withReducer(
    on(TASK_DETAIL_EVENTS.patchTaskDetail, ({ payload }) => ({
      error: payload.error,
      lastRefreshedTaskId: payload.lastRefreshedTaskId,
      loadingTaskDetail: false,
      messageCommunicationEstablished: payload.messageCommunicationEstablished,
      taskDetail: payload.taskDetail ? { ...payload.taskDetail } : null,
    })),
  ),
  withComputed(({ ...store }) => ({
    $error: computed(() => store.error()),
    $exportText: computed(() => {
      const taskDetail = store.taskDetail();
      if (!taskDetail) {
        return 'Export all threads';
      }
      return 'Export single thread';
    }),
    $filteredTask: computed(() => store.filteredTask()),
    $isLoadingMessages: computed(() => store.loadingMessages()),
    $isLoadingTaskDetail: computed(() => store.loadingTaskDetail()),
    $lastRefreshedTaskId: computed(() => store.lastRefreshedTaskId()),
    $messageCommunicationEstablished: computed(() =>
      store.messageCommunicationEstablished(),
    ),
    $messages: computed(() => store.messages()),
    $questionThreadLookup: computed(() => store.questionThreadLookup()),
    $taskDetail: computed(() => store.taskDetail()),
  })),
  withMethods(({ store }) => ({
    updateTaskDetailFilteredState: (filteredTask: boolean) => {
      patchState(store, { filteredTask });
    },
  })),
) satisfies Type<TaskDetailSignalState>;
